import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, ArrowRight, Plane } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ISO2_FLAG = (iso2) => {
  if (!iso2 || iso2.length !== 2) return '';
  const A = 0x1F1E6;
  return String.fromCodePoint(...iso2.toUpperCase().split('').map(c => A + c.charCodeAt(0) - 65));
};

const formatINR = (n) => (typeof n === 'number'
  ? '₹' + n.toLocaleString('en-IN')
  : null);

/**
 * Append Unsplash's runtime transform params to switch JPEG → WebP.
 * Lighthouse flagged ~270KB of savings on the homepage card grid from
 * this single change. Untouched for non-Unsplash hosts (Pexels etc.
 * use a different param contract).
 */
const toWebpUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('images.unsplash.com')) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}fm=webp&q=70`;
};

const DestinationCard = ({ d, hero = false }) => {
  // Card heights are managed by the parent grid. Hero card stays taller via
  // the col-span class on the wrapper; we just fill it.
  return (
    <Link
      to={d.canonical_path}
      data-testid={`popular-destination-${d.slug}`}
      className={`group relative overflow-hidden rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 block ${hero ? 'min-h-[300px] sm:min-h-[420px]' : 'min-h-[180px] sm:min-h-[200px]'}`}
    >
      {/* Cover image OR gradient fallback.
          Rendered as a real <img> (not background-image) so the browser
          can lazy-load it natively and so Lighthouse can apply WebP +
          dimension warnings correctly. ~270KB saved on the homepage
          card grid by switching Unsplash JPEG → WebP. */}
      {d.cover_image ? (
        <img
          src={toWebpUrl(d.cover_image)}
          alt={d.name ? `${d.name} travel destination` : ''}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110"
          aria-hidden={d.name ? undefined : 'true'}
        />
      ) : (
        <div
          className="absolute inset-0 transition-transform duration-500 group-hover:scale-110"
          style={{
            background: 'linear-gradient(135deg, #1B375B 0%, #335E77 60%, #659A9B 100%)',
          }}
          aria-hidden="true"
        >
          <div className="absolute right-4 bottom-4 opacity-15 text-white">
            <Plane className="w-24 h-24" />
          </div>
        </div>
      )}

      {/* Bottom gradient overlay for text legibility */}
      <div
        className="absolute inset-0 bg-gradient-to-t from-[#0B1E36]/90 via-[#0B1E36]/35 to-transparent"
        aria-hidden="true"
      />

      {/* Top-right: fare hint chip */}
      {d.min_fare_inr && (
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm shadow-sm">
          <span className="text-[10px] font-semibold text-gray-500 mr-1">from</span>
          <span className="text-xs font-extrabold text-[#1B375B]">{formatINR(d.min_fare_inr)}</span>
        </div>
      )}

      {/* Bottom: text + IATA chip */}
      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-1 text-white/85">
          <MapPin className="w-3.5 h-3.5" />
          <span className="text-xs font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {ISO2_FLAG(d.country_iso2)} {d.country || '—'}
          </span>
        </div>
        <h3
          className={`text-white font-extrabold leading-tight ${hero ? 'text-3xl sm:text-4xl' : 'text-xl sm:text-2xl'}`}
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {d.name}
        </h3>
        <div className="mt-2 flex items-center gap-2">
          {d.iata && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/15 backdrop-blur-sm text-[10px] font-bold tracking-wider text-white font-mono">
              ✈ {d.iata}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-xs text-white/85 group-hover:text-white">
            Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const Skeleton = ({ hero = false }) => (
  <div className={`rounded-2xl bg-gradient-to-br from-gray-200 to-gray-100 animate-pulse ${hero ? 'min-h-[300px] sm:min-h-[420px]' : 'min-h-[180px] sm:min-h-[200px]'}`} />
);

const PopularDestinations = () => {
  const [destinations, setDestinations] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/homepage-strips`, {
          params: { airlines_limit: 1, destinations_limit: 5 },
          timeout: 6000,
        });
        if (cancelled) return;
        setDestinations(r.data?.popular_destinations || []);
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) return null;
  const list = destinations;

  return (
    <section
      data-testid="popular-destinations-section"
      className="bg-[#FAFCFC] py-12 sm:py-16"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6 sm:mb-8">
          <div>
            <p className="text-[11px] font-bold tracking-[0.18em] text-[#659A9B] uppercase mb-1.5">
              Inspiration for your next trip
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Popular Destinations
            </h2>
            <p className="text-sm text-gray-600 mt-1 max-w-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Cities Indian travelers can&apos;t stop booking — from Bangkok beaches to Saudi pilgrimages.
            </p>
          </div>
          <Link
            to="/destinations"
            data-testid="popular-destinations-view-all"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B375B] hover:text-[#659A9B] transition-colors whitespace-nowrap"
          >
            See all destinations <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {!list ? (
          /* Loading skeleton — matches final layout */
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="col-span-2 row-span-2 sm:col-span-2 sm:row-span-2"><Skeleton hero /></div>
            {[0, 1, 2, 3, 4].map((i) => (<div key={i}><Skeleton /></div>))}
          </div>
        ) : list.length === 0 ? null : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Hero card spans 2x2 on lg, 2x2 on sm. On mobile it's 2-wide
                1-tall above the smaller cards for an Instagram-feed feel. */}
            <div className="col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2">
              <DestinationCard d={list[0]} hero />
            </div>
            {list.slice(1, 5).map((d) => (
              <div key={d.slug}><DestinationCard d={d} /></div>
            ))}
          </div>
        )}

        <Link
          to="/destinations"
          className="sm:hidden mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B375B] hover:text-[#659A9B]"
        >
          See all destinations <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export default PopularDestinations;
